/**
 * Calendar dates and the portal clock, one place for the whole web app.
 *
 * Two kinds of value travel as strings, and they need opposite handling:
 *
 *   - A CALENDAR DATE (`service_date`, `due_date`, a `date` column): a day,
 *     with no time and no zone. It reads `2026-10-26`, or `2026-10-26T00:00:00`
 *     when .NET writes a `DateTime` of Kind Unspecified. It is the 26th in
 *     every zone on earth. `new Date('2026-10-26')` parses it as UTC
 *     midnight, so a browser west of UTC (the UK, Germany, the Americas)
 *     printed the 25th: Logs, Flight Check and every page whose `formatDate`
 *     went that way showed the previous day (QA, 2026-10-26). These helpers
 *     never let the browser's zone touch it.
 *
 *   - An INSTANT (`created_at`, `…Z`, `…+08:00`): a moment, printed on the
 *     tenant's portal clock (`tenantLocale.ts`, Asia/Kuala_Lumpur for Asian
 *     Trails), so two operators in two countries read the same day and time.
 *
 * "Today" is today on the portal clock, never the browser's and never UTC.
 */
import { portalTimeZone } from './tenantLocale';

/** `YYYY-MM-DD`, optionally followed by a wall-clock time with NO zone designator. */
const CALENDAR = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;
/** Exactly `YYYY-MM-DD`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export interface CalendarParts {
  y: number;
  m: number;
  d: number;
}

/** True for exactly `YYYY-MM-DD`. */
export function isDateOnly(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value.trim());
}

/**
 * The day a calendar value names, when it is one: `2026-10-26`, or a date with
 * a midnight time and no zone (`2026-10-26T00:00:00`, how the API writes a
 * DATE column). A value with a real time or a zone is an instant: null.
 */
export function calendarParts(value: unknown): CalendarParts | null {
  if (typeof value !== 'string') return null;
  const m = CALENDAR.exec(value.trim());
  if (!m) return null;
  const hasTime = m[4] !== undefined;
  if (hasTime && (Number(m[4]) !== 0 || Number(m[5]) !== 0 || Number(m[6] ?? 0) !== 0)) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

/** `YYYY-MM-DD` of a calendar value, or null. */
export function calendarKey(value: unknown): string | null {
  const p = calendarParts(value);
  return p ? `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}` : null;
}

/**
 * A `Date` to do arithmetic or read `getDate()` / `getDay()` on: a calendar
 * value becomes LOCAL midnight of that day (so the local getters and an
 * un-zoned `toLocaleDateString` print the right day in every browser); any
 * other string is parsed as usual. Null when unreadable.
 */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const p = calendarParts(value);
  const d = p ? new Date(p.y, p.m - 1, p.d) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `parseCalendarDate(value).getTime()`, NaN when unreadable (sorting, timeline positions). */
export function calendarTime(value: string | null | undefined): number {
  return parseCalendarDate(value)?.getTime() ?? NaN;
}

/**
 * Whole days from today on the portal clock to `value` (a calendar date, or
 * an instant's day on the portal clock). Negative = past; null when unreadable.
 */
export function daysFromToday(value: string | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  return daysBetweenIso(portalIso(now), toDateInputValue(value));
}

/**
 * Print a date. A calendar value prints its own day, whatever the browser's
 * zone (formatted at UTC noon in UTC, so no zone can move it). An instant
 * prints the day and time it is on the portal clock. Null when unreadable.
 */
export function formatCalendar(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale: string | undefined = 'en-GB',
): string | null {
  if (!value) return null;
  const p = calendarParts(value);
  if (p) {
    // Only the date fields: a calendar day has no time to print.
    const dayOnly: Intl.DateTimeFormatOptions = { ...options };
    delete dayOnly.timeZone;
    delete dayOnly.hour;
    delete dayOnly.minute;
    delete dayOnly.second;
    delete dayOnly.hour12;
    delete dayOnly.hourCycle;
    delete dayOnly.timeZoneName;
    return new Date(Date.UTC(p.y, p.m - 1, p.d, 12)).toLocaleDateString(locale, { ...dayOnly, timeZone: 'UTC' });
  }
  const out = formatInstant(value, options, locale);
  return out || null;
}

/**
 * A date-time with no zone designator (`2026-10-26T14:30:00`): a wall-clock
 * reading, as its own fields. Null for anything that carries a zone.
 */
function wallClock(value: string): Date | null {
  const m = CALENDAR.exec(value.trim());
  if (!m || m[4] === undefined) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0)));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Print an instant on the portal clock. A string with no zone designator is a
 * wall-clock reading and prints as written, in every browser (in Kuala Lumpur
 * that is what the browser printed before).
 */
export function formatInstant(
  at: Date | string | number,
  options: Intl.DateTimeFormatOptions,
  locale: string | undefined = 'en-GB',
): string {
  if (typeof at === 'string') {
    const wall = wallClock(at);
    if (wall) return wall.toLocaleString(locale, { ...options, timeZone: 'UTC' });
  }
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, { ...options, timeZone: portalTimeZone() });
}

/**
 * A `YYYY-MM-DD` as seen on the portal clock (`en-CA`'s short date IS
 * `YYYY-MM-DD`; assembled from parts in case a runtime disagrees). Never
 * `toISOString()`, which is the UTC day.
 */
export function portalIso(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: portalTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Today on the portal clock (`YYYY-MM-DD`). */
export function portalTodayIso(now: Date = new Date()): string {
  return portalIso(now);
}

/** Tomorrow on the portal clock (`YYYY-MM-DD`): a calendar step, so no DST hour can skip or repeat a day. */
export function portalTomorrowIso(now: Date = new Date()): string {
  return addDaysIso(portalIso(now), 1);
}

/** This month on the portal clock (`YYYY-MM`). */
export function portalMonthIso(now: Date = new Date()): string {
  return portalIso(now).slice(0, 7);
}

/** This year on the portal clock. */
export function portalYear(now: Date = new Date()): number {
  return Number(portalIso(now).slice(0, 4));
}

/**
 * Shift a `YYYY-MM-DD` by ±N days. Pure calendar arithmetic in UTC on both
 * ends, so no zone can drift it. An unreadable input returns it unchanged.
 */
export function addDaysIso(iso: string, deltaDays: number): string {
  const p = calendarParts(iso);
  if (!p) return iso;
  const dt = new Date(Date.UTC(p.y, p.m - 1, p.d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

/** Whole days from `a` to `b` (both calendar dates); null when either is unreadable. */
export function daysBetweenIso(a: string, b: string): number | null {
  const pa = calendarParts(a);
  const pb = calendarParts(b);
  if (!pa || !pb) return null;
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86_400_000);
}

/**
 * The `YYYY-MM-DD` an `<input type="date">` should hold for a stored value: a
 * calendar date as it is, an instant as its day on the portal clock, '' when
 * unreadable. Never via the browser's zone, or an edit form west of UTC loads
 * (and saves back) the previous day.
 */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  // A calendar date, or a wall-clock reading: its day is literal.
  const m = CALENDAR.exec(value.trim());
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : portalIso(d);
}

/** `YYYY-MM-DD HH:mm` of an instant on the portal clock (24-hour); '' when unreadable. */
export function portalStamp(at: Date | string | number): string {
  const wall = typeof at === 'string' ? wallClock(at) : null;
  const d = wall ?? (at instanceof Date ? at : new Date(at));
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: wall ? 'UTC' : portalTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
}

/** The local-midnight `Date` of a `YYYY-MM-DD` (date pickers, `getDay()`); invalid input = Invalid Date. */
export function localMidnight(iso: string): Date {
  return parseCalendarDate(iso) ?? new Date(NaN);
}
